import React, { useEffect, useState, useRef } from 'react';
import { Grid, Box, LinearProgress, useMediaQuery, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import TruckTurnaroundCard from '../../../components/TruckTurnaroundCard';
import LineChartToggle from './lineChartToggle';
import {
  unsafeMheCounts,
  idleManPower,
  dockUtilization,
  truckInfo,
  ppeKitSafetyVest,
  futureMetrics
} from '../../../api/index';
import { CgSpinner } from 'react-icons/cg';
import { FaCheck } from 'react-icons/fa6';
import { RxCross2 } from 'react-icons/rx';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import MHEAvailabilityCard from '../../../components/MHEAvailabilityCard';
import DeskAvailabilityCard from '../../../components/DeskAvailabilityCard';
import RestAreaCard from '../../../components/RestAreaCard';
import FutureMetricCard from '../../../components/FutureMetricCard';
import PackingStation from '../../../components/PackingStationCard';
import DatePickerStore from './Calendar';

const StorePage = () => {
  const theme = useTheme();
  const navigateTo = useNavigate();
  const location = useLocation();
  const date = useSelector((state) => state.customization.currentDate);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Get page title based on current route
  const getPageTitle = () => {
    if (location.pathname === '/main/stores/evidence') {
      return 'Evidence';
    }
    return 'Overview';
  };

  const [loadingStates, setLoadingStates] = useState({
    authenticated: false,
    status: { loading: true, verified: false },
    dataLoading: true
  });

  const [kpiData, setKpiData] = useState({
    truckData: null,
    mheData: null,
    deskData: null,
    restAreaData: null,
    futureMetricData: null
  });

  // Navigation state and refs
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Check scroll position to show/hide arrows
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  // Smooth scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const cardWidth = isMobile ? 280 : isTablet ? 320 : 416;
      const gap = isMobile ? 8 : isTablet ? 12 : 20;
      const scrollAmount = cardWidth + gap;
      
      scrollContainerRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const cardWidth = isMobile ? 280 : isTablet ? 320 : 416;
      const gap = isMobile ? 8 : isTablet ? 12 : 20;
      const scrollAmount = cardWidth + gap;
      
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Glassmorphism styles
  const glassMorphismStyles = {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
    borderRadius: '16px'
  };

  const authContainerStyles = {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: '0 12px 40px 0 rgba(31, 38, 135, 0.4)',
    borderRadius: '20px'
  };

  const handleAuthError = () => {
    setLoadingStates((prev) => ({ ...prev, status: { loading: false, verified: false } }));
    if (localStorage.getItem('accessToken')) localStorage.removeItem('accessToken');
    const timeoutId = setTimeout(() => navigateTo('/'), 1500);
    return () => clearTimeout(timeoutId);
  };

  const AuthorizationAPI = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      navigateTo('/');
      return;
    }

    try {
      let data = [];
      try {
        data = await axios.post(
          `https://db.neophyte.live/fm-api/users/start_session`,
          {},
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
      } catch {
        // retry path already tried above – keep silent logging if needed
      }

      if (data.data?.message === 'Session created successfully') {
        setLoadingStates((prev) => ({ ...prev, status: { loading: false, verified: true } }));
        setTimeout(() => setLoadingStates((prev) => ({ ...prev, authenticated: true })), 1500);
      } else {
        handleAuthError();
      }
    } catch (error) {
      console.error('Error starting session:', error);
      handleAuthError();
    }
  };

  useEffect(() => {
    AuthorizationAPI();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      setLoadingStates((prev) => ({ ...prev, dataLoading: true }));
      try {
        const [truckIdle, unsafeMhe, ppe_kit, idle_man_power, dock_utilization, future_metrics] = await Promise.all([
          truckInfo({ date }),
          unsafeMheCounts({ date }),
          ppeKitSafetyVest({ date }),
          idleManPower({ date }),
          dockUtilization({ date }),
          futureMetrics({ date })
        ]);

        setKpiData({
          truckData: truckIdle,
          mheData: { date: unsafeMhe },
          deskData: ppe_kit,
          restAreaData: { idleManPower: idle_man_power, dockUtilization: dock_utilization },
          futureMetricData: future_metrics
        });
      } catch (error) {
        console.error('Error fetching KPI data:', error);
      } finally {
        setLoadingStates((prev) => ({ ...prev, dataLoading: false }));
      }
    };

    if (loadingStates.authenticated) fetchData();

    return () => controller.abort();
  }, [date, loadingStates.authenticated]);

  // Check scroll position on mount and resize
  useEffect(() => {
    const handleResize = () => {
      setTimeout(checkScrollPosition, 100);
    };

    checkScrollPosition();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, [loadingStates.authenticated]);

  if (!loadingStates.authenticated) {
    return (
      <div className="w-screen h-screen flex justify-center place-items-center" style={{ backgroundColor: 'transparent' }}>
        <div className="px-8 py-6 flex justify-center place-items-center" style={authContainerStyles}>
          <span className="text-white font-medium">
            {loadingStates.status.loading
              ? 'Authenticating, please wait...'
              : loadingStates.status.verified
              ? 'Authenticated successfully, redirecting...'
              : 'Authentication failed, please try again...'}
          </span>
          {loadingStates.status.loading ? (
            <CgSpinner className="ml-4 text-xl text-cyan-300 animate-spin" />
          ) : loadingStates.status.verified ? (
            <FaCheck className="ml-4 text-xl text-emerald-400" />
          ) : (
            <RxCross2 className="ml-4 text-xl text-rose-400" />
          )}
        </div>
      </div>
    );
  }

  if (loadingStates.dataLoading) {
    return (
      <div style={{ display: 'flex', backgroundColor: 'transparent' }}>
        <div
          style={{
            flexGrow: 1,
            padding: '20px',
            marginLeft: isMobile ? '0px' : '64px',
            width: isMobile ? '100%' : 'calc(100% - 64px)',
            marginTop: '0',
            transition: 'margin-left 0.3s ease, width 0.3s ease'
          }}
        >
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Box sx={glassMorphismStyles}>
                <LinearProgress
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    '& .MuiLinearProgress-bar': { backgroundColor: 'rgba(6, 182, 212, 0.8)' }
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </div>
      </div>
    );
  }

  return (
    <div className="flex" style={{ backgroundColor: 'transparent' }}>
      <div
        className="w-full"
        style={{
          marginLeft: isMobile ? '0px' : '64px',
          marginTop: '0',
          padding: isMobile ? '12px' : '20px',
          transition: 'margin-left 0.3s ease, width 0.3s ease',
          minWidth: 0, // Prevent shrinking
          flex: 1
        }}
      >
        <Box sx={{ width: '100%', backgroundColor: 'transparent' }}>
          {/* Page header */}
          <Box
            sx={{
              px: isMobile ? 1 : 2,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              marginTop: -5,
              marginLeft: -10
            }}
          >
            <h1
              style={{
                fontFamily: 'Plus Jakarta Sans',
                fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
                fontWeight: 600,
                margin: 0,
                background: 'linear-gradient(135deg,#fff 0%,rgba(255,255,255,.85) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-.025em',
                lineHeight: 1.2
              }}
            >
              {getPageTitle()}
            </h1>

            <DatePickerStore />
          </Box>

          {/* KPI Cards - Responsive Layout with Navigation */}
          <Box
            sx={{
              mb: 3,
              marginLeft: isMobile ? '0px' : '-70px',
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? 1 : 2
            }}
          >
            {/* Left Navigation Arrow */}
            {canScrollLeft && !isMobile && (
              <IconButton
                onClick={scrollLeft}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  // backdropFilter: 'blur(16px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                  // transition: 'all 0.3s ease',
                  flexShrink: 0,
                  // '&:hover': {
                  //   backgroundColor: 'rgba(54, 197, 240, 0.2)',
                  //   border: '1px solid rgba(54, 197, 240, 0.4)',
                  //   transform: 'scale(1.05)',
                  //   boxShadow: '0 6px 20px rgba(54, 197, 240, 0.3)'
                  // },
                  '&:active': {
                    transform: 'scale(0.95)'
                  }
                }}
              >
                <FaChevronLeft size={16} />
              </IconButton>
            )}

            {/* Scrollable Cards Container */}
            <Box
              ref={scrollContainerRef}
              onScroll={checkScrollPosition}
              sx={{
                flex: 1,
                overflowX: 'auto',
                overflowY: 'hidden',
                '&::-webkit-scrollbar': {
                  display: 'none'
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                scrollBehavior: 'smooth'
              }}
            >
              {/* Mobile Navigation Arrows - Inside container for mobile */}
              {isMobile && (
                <Box sx={{ position: 'relative' }}>
                  {canScrollLeft && (
                    <IconButton
                      onClick={scrollLeft}
                      sx={{
                        position: 'absolute',
                        left: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        // backdropFilter: 'blur(16px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                        // transition: 'all 0.3s ease',
                        // '&:hover': {
                        //   backgroundColor: 'rgba(54, 197, 240, 0.2)',
                        //   border: '1px solid rgba(54, 197, 240, 0.4)',
                        //   transform: 'translateY(-50%) scale(1.05)',
                        //   boxShadow: '0 6px 20px rgba(54, 197, 240, 0.3)'
                        // }
                      }}
                    >
                      <FaChevronLeft size={14} />
                    </IconButton>
                  )}
                  
                  {canScrollRight && (
                    <IconButton
                      onClick={scrollRight}
                      sx={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        // backdropFilter: 'blur(16px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                        // transition: 'all 0.3s ease',
                        // '&:hover': {
                        //   backgroundColor: 'rgba(54, 197, 240, 0.2)',
                        //   border: '1px solid rgba(54, 197, 240, 0.4)',
                        //   transform: 'translateY(-50%) scale(1.05)',
                        //   boxShadow: '0 6px 20px rgba(54, 197, 240, 0.3)'
                        // }
                      }}
                    >
                      <FaChevronRight size={14} />
                    </IconButton>
                  )}
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  gap: isMobile ? 1 : isTablet ? 1.5 : 2.5,
                  minWidth: 'fit-content',
                  pb: 1,
                  px: isMobile ? 1 : 0
                }}
              >
              {/* Dock Utilization Card */}
              <Box
                sx={{
                  width: isMobile ? '280px' : isTablet ? '320px' : '416px',
                  height: isMobile ? '240px' : isTablet ? '260px' : '287px',
                  flexShrink: 0
                }}
              >
                <TruckTurnaroundCard
                  data={kpiData.truckData}
                  loading={loadingStates.dataLoading}
                  width="100%"
                  height="100%"
                  maxWidth={isMobile ? '280px' : isTablet ? '320px' : '416px'}
                  maxHeight={isMobile ? '240px' : isTablet ? '260px' : '287px'}
                />
              </Box>

              {/* Safety Breaches Card */}
              <Box
                sx={{
                  width: isMobile ? '280px' : isTablet ? '320px' : '416px',
                  height: isMobile ? '240px' : isTablet ? '260px' : '287px',
                  flexShrink: 0
                }}
              >
                <DeskAvailabilityCard
                  data={kpiData.deskData}
                  loading={loadingStates.dataLoading}
                  width="100%"
                  height="100%"
                  maxWidth={isMobile ? '280px' : isTablet ? '320px' : '416px'}
                  maxHeight={isMobile ? '240px' : isTablet ? '260px' : '287px'}
                />
              </Box>



               {/* Truck Count Card */}
               <Box
                sx={{
                  width: isMobile ? '280px' : isTablet ? '320px' : '416px',
                  height: isMobile ? '240px' : isTablet ? '260px' : '287px',
                  flexShrink: 0
                }}
              >
                <RestAreaCard
                  data={kpiData.restAreaData}
                  loading={loadingStates.dataLoading}
                  width="100%"
                  height="100%"
                  maxWidth={isMobile ? '280px' : isTablet ? '320px' : '416px'}
                  maxHeight={isMobile ? '240px' : isTablet ? '260px' : '287px'}
                />
              </Box>


               {/* Dock Utilization Card */}
               <Box
                sx={{
                  width: isMobile ? '280px' : isTablet ? '320px' : '416px',
                  height: isMobile ? '240px' : isTablet ? '260px' : '287px',
                  flexShrink: 0
                }}
              >
                <PackingStation
                  data={kpiData.truckData}
                  loading={loadingStates.dataLoading}
                  selectedDate={date}
                  width="100%"
                  height="100%"
                  maxWidth={isMobile ? '280px' : isTablet ? '320px' : '416px'}
                  maxHeight={isMobile ? '240px' : isTablet ? '260px' : '287px'}
                />
              </Box>

              {/* Unsafe MHE Card */}
              <Box
                sx={{
                  width: isMobile ? '280px' : isTablet ? '320px' : '416px',
                  height: isMobile ? '240px' : isTablet ? '260px' : '287px',
                  flexShrink: 0
                }}
              >
                <MHEAvailabilityCard
                  data={kpiData.mheData}
                  loading={loadingStates.dataLoading}
                  width="100%"
                  height="100%"
                  maxWidth={isMobile ? '280px' : isTablet ? '320px' : '416px'}
                  maxHeight={isMobile ? '240px' : isTablet ? '260px' : '287px'}
                />
              </Box>

             

              {/* Future Metrics Card */}
              <Box
                sx={{
                  width: isMobile ? '280px' : isTablet ? '320px' : '416px',
                  height: isMobile ? '240px' : isTablet ? '260px' : '287px',
                  flexShrink: 0
                }}
              >
                <FutureMetricCard
                  data={kpiData.futureMetricData}
                  loading={loadingStates.dataLoading}
                  width="100%"
                  height="100%"
                  maxWidth={isMobile ? '280px' : isTablet ? '320px' : '416px'}
                  maxHeight={isMobile ? '240px' : isTablet ? '260px' : '287px'}
                />
              </Box>
            </Box>
            </Box>

            {/* Right Navigation Arrow */}
            {canScrollRight && !isMobile && (
              <IconButton
                onClick={scrollRight}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  // backdropFilter: 'blur(16px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                  // transition: 'all 0.3s ease',
                  flexShrink: 0,
                  // '&:hover': {
                  //   backgroundColor: 'rgba(54, 197, 240, 0.2)',
                  //   border: '1px solid rgba(54, 197, 240, 0.4)',
                  //   transform: 'scale(1.05)',
                  //   boxShadow: '0 6px 20px rgba(54, 197, 240, 0.3)'
                  // },
                  '&:active': {
                    transform: 'scale(0.95)'
                  }
                }}
              >
                <FaChevronRight size={16} />
              </IconButton>
            )}
          </Box>

          {/* Charts */}
          <Box sx={{ 
            width: isMobile ? '100%' : 'calc(100% + 90px)', 
            mt: 1.5, 
            marginLeft: isMobile ? '0px' : '-70px', 
            ...glassMorphismStyles, 
            p: isMobile ? 1 : 2 
          }}>
            <LineChartToggle />
          </Box>
        </Box>
      </div>
    </div>
  );
};

export default StorePage;
